/**
 * Script Actions
 *
 * Actions for generating SQL scripts (CREATE, ALTER, DROP, etc.)
 */

import { FilePlus, FileEdit, Trash, Play } from 'lucide-react';
import type { ObjectActionDefinition, ObjectActionContext } from '../types';
import { useWorkspaceStore } from '../../../workspace/core/store/workspaceStore';
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import { getScriptProvider } from '../../../scripting';
import { toast } from '../../../../components/ui/toaster';
import { safeConnectionAlias, safeConnectionServer } from '../../../../types/safeConnectionDisplay';

/**
 * Helper to open a query tab with script content
 */
function openScriptTab(
  context: ObjectActionContext,
  script: string,
  titleSuffix: string,
  isGenerating = false,
): string {
  const { activeConnection, connection } = getConnectionById(context.connectionId);

  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return '';
  }

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  return addTab({
    title: `${context.objectName}_${titleSuffix}-${queryNumber}`,
    type: 'query',
    content: {
      query: script,
      isGeneratingScript: isGenerating,
    },
    connectionInfo: {
      connectionId: context.connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: context.databaseName,
    },
  });
}

/**
 * Script CREATE action - generates CREATE script
 */
export const scriptCreateAction: ObjectActionDefinition = {
  id: 'script-create',
  label: 'Script as CREATE',
  icon: FilePlus,
  category: 'script',
  appliesTo: ['table', 'view', 'procedure', 'function'],
  execute: async (context: ObjectActionContext) => {
    const { activeConnection, connection, engine } = getConnectionById(context.connectionId);

    if (!activeConnection || !connection) {
      toast.error({ title: 'Error', description: 'Connection not found' });
      return;
    }

    const { updateTabContent } = useWorkspaceStore.getState();

    // For tables, use table script provider
    if (context.objectType === 'table') {
      const tabId = openScriptTab(
        context,
        `-- Generating CREATE script for ${context.schemaName}.${context.objectName}...\n`,
        'CREATE',
        true,
      );

      try {
        // Fetch table metadata if available
        let tableMetadata: import('../../../scripting').TableMetadata | undefined;
        if (window.electronAPI?.getTableMetadata) {
          const result = await window.electronAPI.getTableMetadata({
            connectionId: context.connectionId,
            database: context.databaseName,
            schema: context.schemaName,
            tableName: context.objectName,
          });
          if (result.success && result.metadata) {
            tableMetadata = result.metadata;
          }
        }

        const provider = getScriptProvider(engine);
        const script = provider.generateTableScript('create', {
          database: context.databaseName,
          schema: context.schemaName,
          tableName: context.objectName,
          metadata: tableMetadata,
        });

        updateTabContent(tabId, { query: script, isGeneratingScript: false });
      } catch (error) {
        updateTabContent(tabId, {
          query: `-- Error generating script: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
          isGeneratingScript: false,
        });
        toast.error({
          title: 'Script Generation Error',
          description: error instanceof Error ? error.message : 'Failed to generate script',
        });
      }
      return;
    }

    // For Oracle package members, fetch the parent package source
    if (engine === 'oracle' && context.packageName) {
      const tabId = openScriptTab(
        context,
        `-- Loading definition for ${context.packageName}.${context.objectName}...\n`,
        'CREATE',
        true,
      );

      try {
        const { fetchOracleObjectScript } = await import(
          '../../../database-objects/services/objectDefinitionService'
        );

        const { script, error } = await fetchOracleObjectScript({
          connectionId: context.connectionId,
          database: context.databaseName,
          schema: context.schemaName,
          name: context.packageName,
          objectType: 'package',
          mode: 'create',
        });

        updateTabContent(tabId, { query: script, isGeneratingScript: false });
        if (error) {
          toast.error({ title: 'Script Error', description: error });
        }
      } catch (error) {
        updateTabContent(tabId, {
          query: `-- Error: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
          isGeneratingScript: false,
        });
      }
      return;
    }

    // For views, procedures, functions - fetch definition
    const tabId = openScriptTab(
      context,
      `-- Loading CREATE script for ${context.schemaName}.${context.objectName}...\n`,
      'CREATE',
      true,
    );

    try {
      const { fetchObjectDefinitionScript } = await import(
        '../../../database-objects/services/objectDefinitionService'
      );

      const objectTypeMap: Record<string, 'view' | 'stored-procedure' | 'function'> = {
        view: 'view',
        procedure: 'stored-procedure',
        function: 'function',
      };

      const { script, error } = await fetchObjectDefinitionScript({
        connectionId: context.connectionId,
        database: context.databaseName,
        engine,
        objectType: objectTypeMap[context.objectType] || 'view',
        schema: context.schemaName,
        name: context.objectName,
        objectId: context.objectId,
        mode: 'create',
      });

      updateTabContent(tabId, { query: script, isGeneratingScript: false });
      if (error) {
        toast.error({ title: 'Script Error', description: error });
      }
    } catch (error) {
      updateTabContent(tabId, {
        query: `-- Error: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
        isGeneratingScript: false,
      });
    }
  },
};

/**
 * Script ALTER action - generates ALTER script
 */
export const scriptAlterAction: ObjectActionDefinition = {
  id: 'script-alter',
  label: 'Script as ALTER',
  icon: FileEdit,
  category: 'script',
  appliesTo: ['view', 'procedure', 'function'],
  execute: async (context: ObjectActionContext) => {
    const { engine } = getConnectionById(context.connectionId);
    const { updateTabContent } = useWorkspaceStore.getState();

    // Oracle package members: show the full package (ALTER not applicable to individual members)
    if (engine === 'oracle' && context.packageName) {
      const tabId = openScriptTab(
        context,
        `-- Loading package source for ${context.packageName}.${context.objectName}...\n`,
        'ALTER',
        true,
      );

      try {
        const { fetchOracleObjectScript } = await import(
          '../../../database-objects/services/objectDefinitionService'
        );

        const { script, error } = await fetchOracleObjectScript({
          connectionId: context.connectionId,
          database: context.databaseName,
          schema: context.schemaName,
          name: context.packageName,
          objectType: 'package',
          mode: 'create',
        });

        updateTabContent(tabId, { query: script, isGeneratingScript: false });
        if (error) {
          toast.error({ title: 'Script Error', description: error });
        }
      } catch (error) {
        updateTabContent(tabId, {
          query: `-- Error: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
          isGeneratingScript: false,
        });
      }
      return;
    }

    const tabId = openScriptTab(
      context,
      `-- Loading ALTER script for ${context.schemaName}.${context.objectName}...\n`,
      'ALTER',
      true,
    );

    try {
      const { fetchObjectDefinitionScript } = await import(
        '../../../database-objects/services/objectDefinitionService'
      );

      const objectTypeMap: Record<string, 'view' | 'stored-procedure' | 'function'> = {
        view: 'view',
        procedure: 'stored-procedure',
        function: 'function',
      };

      const { script, error } = await fetchObjectDefinitionScript({
        connectionId: context.connectionId,
        database: context.databaseName,
        engine,
        objectType: objectTypeMap[context.objectType] || 'view',
        schema: context.schemaName,
        name: context.objectName,
        objectId: context.objectId,
        mode: 'alter',
      });

      updateTabContent(tabId, { query: script, isGeneratingScript: false });
      if (error) {
        toast.error({ title: 'Script Error', description: error });
      }
    } catch (error) {
      updateTabContent(tabId, {
        query: `-- Error: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
        isGeneratingScript: false,
      });
    }
  },
};

/**
 * Script DROP action - generates DROP script
 */
export const scriptDropAction: ObjectActionDefinition = {
  id: 'script-drop',
  label: 'Script as DROP',
  icon: Trash,
  category: 'script',
  appliesTo: ['table', 'view', 'procedure', 'function'],
  execute: async (context: ObjectActionContext) => {
    const { engine } = getConnectionById(context.connectionId);
    const provider = getScriptProvider(engine);
    const qualified = provider.qualifyTableName(context.schemaName, context.objectName);

    // Map object type to SQL keyword
    const keywordMap: Record<string, string> = {
      table: 'TABLE',
      view: 'VIEW',
      procedure: 'PROCEDURE',
      function: 'FUNCTION',
    };

    const keyword = keywordMap[context.objectType] || 'OBJECT';

    // For PostgreSQL routines, we need to fetch parameters for the signature
    if (engine === 'pgsql' && (context.objectType === 'procedure' || context.objectType === 'function')) {
      const { updateTabContent } = useWorkspaceStore.getState();
      const tabId = openScriptTab(
        context,
        `-- Loading DROP script for ${context.schemaName}.${context.objectName}...\n`,
        'DROP',
        true,
      );

      try {
        const { fetchRoutineParameters } = await import(
          '../../../database-objects/services/objectDefinitionService'
        );

        const { identityArguments } = await fetchRoutineParameters({
          connectionId: context.connectionId,
          database: context.databaseName,
          engine,
          routineType: context.objectType === 'procedure' ? 'stored-procedure' : 'function',
          schema: context.schemaName,
          name: context.objectName,
          objectId: context.objectId,
        });

        const args = (identityArguments ?? '').trim();
        const signature = `(${args})`;
        const drop = `DROP ${keyword} IF EXISTS ${qualified}${signature};\n`;

        updateTabContent(tabId, { query: drop, isGeneratingScript: false });
      } catch (error) {
        const fallback = `DROP ${keyword} IF EXISTS ${qualified};\n`;
        updateTabContent(tabId, {
          query: `-- Note: Could not fetch signature\n${fallback}`,
          isGeneratingScript: false,
        });
      }
      return;
    }

    // For Oracle, use engine-specific DROP syntax (no IF EXISTS support)
    if (engine === 'oracle') {
      if (context.objectType === 'table') {
        const script = provider.generateTableScript('drop', {
          database: context.databaseName,
          schema: context.schemaName,
          tableName: context.objectName,
        });
        openScriptTab(context, script, 'DROP');
        return;
      }
      // Package members cannot be dropped individually — drop the whole package
      if (context.packageName) {
        const pkgQualified = provider.qualifyTableName(context.schemaName, context.packageName);
        const script = `-- Individual package members cannot be dropped.\n-- To remove ${context.objectName}, edit the package source.\n-- To drop the entire package:\nDROP PACKAGE ${pkgQualified};\n`;
        openScriptTab(context, script, 'DROP');
        return;
      }
      const script = `DROP ${keyword} ${qualified};\n`;
      openScriptTab(context, script, 'DROP');
      return;
    }

    // Simple DROP for tables, views, and non-PostgreSQL routines
    const script = `DROP ${keyword} IF EXISTS ${qualified};\n`;
    openScriptTab(context, script, 'DROP');
  },
};

/**
 * Execute action - generates EXEC/CALL template for routines
 */
export const executeRoutineAction: ObjectActionDefinition = {
  id: 'execute',
  label: 'Execute...',
  icon: Play,
  category: 'script',
  appliesTo: ['procedure'],
  isPrimary: true,
  execute: async (context: ObjectActionContext) => {
    const { activeConnection, connection, engine } = getConnectionById(context.connectionId);

    if (!activeConnection || !connection) {
      toast.error({ title: 'Error', description: 'Connection not found' });
      return;
    }

    const { updateTabContent } = useWorkspaceStore.getState();
    const provider = getScriptProvider(engine);
    const qualified = provider.qualifyTableName(context.schemaName, context.objectName);

    const tabId = openScriptTab(
      context,
      `-- Loading parameters for ${context.schemaName}.${context.objectName}...\n`,
      'EXEC',
      true,
    );

    try {
      const { fetchRoutineParameters } = await import(
        '../../../database-objects/services/objectDefinitionService'
      );

      const { parameters } = await fetchRoutineParameters({
        connectionId: context.connectionId,
        database: context.databaseName,
        engine,
        routineType: 'stored-procedure',
        schema: context.schemaName,
        name: context.objectName,
        objectId: context.objectId,
        packageName: context.packageName,
        overloadIndex: context.overloadIndex,
      });

      let template: string;
      if (engine === 'oracle') {
        // For package members, qualify with package name
        const callTarget = context.packageName
          ? `${provider.qualifyTableName(context.schemaName, context.packageName)}.${context.objectName}`
          : qualified;
        const inputParams = parameters.filter((p) => p.direction !== 'OUT');
        const args = inputParams
          .map((p) => (p.name ? `${p.name} => NULL` : 'NULL'))
          .join(', ');
        template = [
          `-- Execute procedure ${callTarget}`,
          `-- Parameters: ${parameters.length === 0 ? '(none)' : ''}`,
          ...parameters.map((p) => `--   ${p.name} ${p.dataType}${p.direction === 'IN' ? '' : ` (${p.direction})`}`),
          '',
          `BEGIN`,
          `    ${callTarget}(${args});`,
          `END;`,
          '/',
          '',
        ].join('\n');
      } else if (engine === 'pgsql') {
        const inputParams = parameters.filter((p) => p.direction !== 'OUT');
        const args = inputParams
          .map((p) => (p.name ? `${p.name} => NULL` : 'NULL'))
          .join(', ');
        template = [
          `-- Execute procedure ${qualified}`,
          `-- Parameters: ${parameters.length === 0 ? '(none)' : ''}`,
          ...parameters.map((p) => `--   ${p.name} ${p.dataType}${p.direction === 'IN' ? '' : ` (${p.direction})`}`),
          '',
          `CALL ${qualified}(${args});`,
          '',
        ].join('\n');
      } else {
        // MSSQL
        if (parameters.length === 0) {
          template = [
            `-- Execute stored procedure ${qualified}`,
            '-- Parameters: (none)',
            '',
            `EXEC ${qualified};`,
            '',
          ].join('\n');
        } else {
          const lines = [`EXEC ${qualified}`];
          parameters.forEach((p, idx) => {
            const comma = idx === parameters.length - 1 ? '' : ',';
            const output = p.direction === 'OUT' ? ' OUTPUT' : '';
            lines.push(`  ${p.name} = <value>${output}${comma}`);
          });
          template = [
            `-- Execute stored procedure ${qualified}`,
            '-- Parameters:',
            ...parameters.map((p) => `--   ${p.name} ${p.dataType}${p.direction === 'IN' ? '' : ` (${p.direction})`}`),
            '',
            ...lines,
            '',
          ].join('\n');
        }
      }

      updateTabContent(tabId, { query: template, isGeneratingScript: false });
    } catch (error) {
      updateTabContent(tabId, {
        query: `-- Error fetching parameters: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
        isGeneratingScript: false,
      });
    }
  },
};

/**
 * All script actions
 */
export const scriptActions: ObjectActionDefinition[] = [
  scriptCreateAction,
  scriptAlterAction,
  scriptDropAction,
  executeRoutineAction,
];
